import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Briefcase, 
  GraduationCap, 
  TrendingUp, 
  Crown, 
  ArrowRight,
  BarChart3,
  Clock
} from 'lucide-react';
import { useAuth } from '../auth/AuthComponents';
import { useUsage } from '../../contexts/UsageContext';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import MobileDashboard from './MobileDashboard';
import { getDocumentDisplayName } from '../../utils/documentDisplayName';
import { getUsageDisplayValues } from '../../utils/planLabels';

const Dashboard = () => {
  const { user } = useAuth();
  const { usage, subscription } = useUsage();
  const [analytics, setAnalytics] = useState({
    totalContracts: 0,
    totalCVs: 0,
    totalSOPs: 0,
    totalRevamps: 0,
    totalGenerated: 0,
    totalAnalyzed: 0,
    thisMonth: {
      contracts: 0,
      cvs: 0,
      sops: 0
    },
    recentDocuments: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchDashboardData = async () => {
    try {
        setLoading(true);
        const analyticsData = await apiRequest('analytics/dashboard');
        
        if (analyticsData) {
          setAnalytics(analyticsData);
        }
      } catch (err) {
        setError(err.message);
        toast.error(`Failed to load dashboard data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
    
    fetchDashboardData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const handleAnalyzeDocument = (type) => {
    navigate(`/document-analysis?type=${type}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCurrentPlanName = () => {
    const plan = subscription?.subscription_plan || user?.subscription_plan;
    if (!plan) return 'Free';
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const { used: usageUsed, limit: usageLimit } = getUsageDisplayValues(usage);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--color-text-muted)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <MobileDashboard
        analytics={analytics}
        subscription={subscription}
        usage={usage}
        user={user}
        loading={loading}
        error={error}
        handleAnalyzeDocument={handleAnalyzeDocument}
        getCurrentPlanName={getCurrentPlanName}
        formatDate={formatDate}
      />
    );
  }

  return (
    <div className="h-full p-6">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--color-text)] mb-4 leading-tight">
            {getGreeting()},{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
              {user?.full_name?.split(' ')[0]}
            </span>
            👋
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] w-full">
            Welcome back to your AI-powered document analysis dashboard. Ready to analyze and optimize your documents?
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[{
            title: 'Analyze Contract',
            description: 'Review risks and improve terms',
            icon: <FileText className="w-7 h-7" />, color: 'from-red-500 to-orange-500', action: () => handleAnalyzeDocument('contract')
          }, {
            title: 'Optimize CV',
            description: 'Match your resume to job roles',
            icon: <Briefcase className="w-7 h-7" />, color: 'from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]', action: () => handleAnalyzeDocument('cv')
          }, {
            title: 'Enhance SOP',
            description: 'Perfect your statement of purpose',
            icon: <GraduationCap className="w-7 h-7" />, color: 'from-green-500 to-teal-500', action: () => handleAnalyzeDocument('sop')
          }].map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className="elevated-card rounded-2xl p-6 hover:border-[var(--color-primary)] transition-all group flex flex-col justify-between h-full text-left"
            >
              <div className="flex items-center w-full mb-1">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <div className="flex-1 flex flex-col items-end ml-4">
                  <span className="text-[var(--color-text)] font-semibold text-lg group-hover:text-[var(--color-primary)] transition-colors text-right">{item.title}</span>
                  <span className="text-[var(--color-text-muted)] text-sm group-hover:text-[var(--color-text-secondary)] transition-colors text-right mt-1">{item.description}</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Stats Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Total Documents Analyzed */}
            <div className="elevated-card rounded-2xl p-6 flex flex-col justify-between h-full">
              <div className="flex items-center w-full mb-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] flex items-center justify-center mr-3">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-[var(--color-text)] mr-3">{analytics.totalContracts + analytics.totalCVs + analytics.totalSOPs}</span>
                <div className="flex-1 flex flex-col items-end">
                  <span className="text-[var(--color-text-muted)] text-sm font-medium text-right">Total Documents Analyzed</span>
                  <span className="text-[var(--color-success)] text-xs font-medium flex items-center text-right mt-1"><TrendingUp className="w-4 h-4 mr-1" />All Time</span>
                </div>
              </div>
            </div>
            {/* Total Documents Revamped */}
            <div className="elevated-card rounded-2xl p-6 flex flex-col justify-between h-full">
              <div className="flex items-center w-full mb-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] flex items-center justify-center mr-3">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-[var(--color-text)] mr-3">{analytics.totalRevamps}</span>
                <div className="flex-1 flex flex-col items-end">
                  <span className="text-[var(--color-text-muted)] text-sm font-medium text-right">Total Documents Revamped</span>
                  <span className="text-[var(--color-success)] text-xs font-medium flex items-center text-right mt-1"><TrendingUp className="w-4 h-4 mr-1" />All Time</span>
                </div>
              </div>
            </div>
            {/* Total Documents Generated */}
            <div className="elevated-card rounded-2xl p-6 flex flex-col justify-between h-full">
              <div className="flex items-center w-full mb-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center mr-3">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-[var(--color-text)] mr-3">{analytics.totalGenerated}</span>
                <div className="flex-1 flex flex-col items-end">
                  <span className="text-[var(--color-text-muted)] text-sm font-medium text-right">Total Documents Generated</span>
                  <span className="text-[var(--color-primary)] text-xs font-medium text-right mt-1">All Time</span>
                </div>
              </div>
            </div>
            {/* Overall Documents Processed */}
            <div className="elevated-card rounded-2xl p-6 flex flex-col justify-between h-full">
              <div className="flex items-center w-full mb-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-accent)] flex items-center justify-center mr-3">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-[var(--color-text)] mr-3">{(analytics.totalAnalyzed ?? (analytics.totalContracts + analytics.totalCVs + analytics.totalSOPs)) + (analytics.totalRevamps ?? 0) + (analytics.totalGenerated ?? 0)}</span>
                <div className="flex-1 flex flex-col items-end">
                  <span className="text-[var(--color-text-muted)] text-sm font-medium text-right">Overall Documents Processed</span>
                  <span className="text-[var(--color-primary)] text-xs font-medium text-right mt-1">All Time</span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="elevated-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--color-text)]">Subscription Status</h3>
              <Crown className="w-6 h-6 text-[var(--color-primary)]" />
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[var(--color-text-muted)] text-sm">Current Plan</span>
                  <span className="text-[var(--color-primary)] font-semibold capitalize">{usage?.subscription_plan ? usage.subscription_plan.charAt(0).toUpperCase() + usage.subscription_plan.slice(1) : 'Free'}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[var(--color-text-muted)] text-sm">Usage</span>
                  <span className="text-[var(--color-text)]">{usageUsed}/{usageLimit}</span>
                </div>
                <div className="w-full bg-[var(--color-border)] rounded-full h-2 mb-4">
                  <div 
                    className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.min(((usageUsed / (usageLimit || 1)) * 100), 100)}%` 
                    }}
                  />
                </div>
              </div>

              {user?.subscription_plan !== 'enterprise' && (
                <button 
                  onClick={() => navigate('/subscriptions')}
                  className="brand-gradient-btn w-full px-4 py-2 rounded-lg flex items-center justify-center"
                >
                  Upgrade Plan
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity & Promo Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 elevated-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-[var(--color-text)]">Recent Analysis Activity</h3>
              <button 
                onClick={() => navigate('/recent-activities')}
                className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm font-medium"
              >
                View All
              </button>
            </div>

            {analytics.recentDocuments.length > 0 ? (
              <div className="space-y-3">
                {analytics.recentDocuments.slice(0, 5).map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg hover:border-[var(--color-border-subtle)] transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        doc.document_type === 'contract' ? 'bg-red-500/20 text-red-400' :
                        doc.document_type === 'cv' ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]' :
                        'bg-green-500/20 text-green-500'
                      }`}>
                        {doc.document_type === 'contract' ? <FileText className="w-4 h-4" /> :
                         doc.document_type === 'cv' ? <Briefcase className="w-4 h-4" /> :
                         <GraduationCap className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-[var(--color-text)] text-sm font-medium truncate max-w-48" title={getDocumentDisplayName(doc)}>
                          {getDocumentDisplayName(doc)}
                        </p>
                        <p className="text-[var(--color-text-muted)] text-xs capitalize">
                          {doc.document_type} • {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      doc.status === 'completed' ? 'bg-green-500/15 text-[var(--color-success)]' :
                      doc.status === 'processing' ? 'bg-amber-500/15 text-[var(--color-warning)]' :
                      'bg-red-500/15 text-[var(--color-danger)]'
                    }`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
                <p className="text-[var(--color-text-muted)] mb-4">No recent activity</p>
                <button 
                  onClick={() => handleAnalyzeDocument('contract')}
                  className="brand-gradient-btn px-6 py-2 rounded-lg"
                >
                  Analyze Your First Document
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
